import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CircularProgress, Alert, Chip, Stack, Divider, LinearProgress
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import FeedbackIcon from '@mui/icons-material/Feedback';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import api from '../../services/api';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';

const roleColor = { admin: '#1a237e', homeowner: '#2e7d32', general_contractor: '#0288d1', subcontractor: '#ef6c00' };
const statusColor = { trial: '#0288d1', active: '#2e7d32', past_due: '#ef6c00', suspended: '#c62828', cancelled: '#9e9e9e' };
const fbColor = { open: '#ef6c00', in_progress: '#0288d1', resolved: '#2e7d32', wont_do: '#9e9e9e' };

function Kpi({ icon, label, value, sub, color = '#1a237e', onClick }) {
  return (
    <Card variant="outlined" sx={{ p: 2.5, height: '100%', borderRadius: 2, cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { borderColor: color, boxShadow: 1 } : {} }} onClick={onClick}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <Box sx={{ bgcolor: `${color}15`, color, p: 1, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
        <Typography variant="caption" sx={{ color: '#666', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>{label}</Typography>
      </Stack>
      <Typography sx={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{value}</Typography>
      {sub && <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.5 }}>{sub}</Typography>}
    </Card>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/admin/stats')
      .then(r => setStats(r.data))
      .catch(e => setError(e.response?.data?.error || 'Failed to load stats'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Layout><Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box></Layout>;
  if (error) return <Layout><Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box></Layout>;
  if (!stats) return null;

  const totalUsers = stats.users_by_role.reduce((s, r) => s + r.cnt, 0);
  const activeMRR = (stats.mrr.active_mrr_cents / 100).toFixed(0);
  const potentialMRR = ((stats.mrr.active_mrr_cents + stats.mrr.trial_potential_mrr_cents) / 100).toFixed(0);
  const subSubs = stats.subscriptions_by_status.reduce((s, r) => s + r.cnt, 0);
  const openFb = stats.feedback_by_status.find(f => f.status === 'open')?.cnt || 0;
  const totalFb = stats.feedback_by_status.reduce((s, r) => s + r.cnt, 0);

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e', mb: 0.5 }}>Admin Dashboard</Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>System overview · {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</Typography>

        {/* Hero KPI row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Kpi icon={<PeopleIcon />} label="Total users" value={totalUsers} sub={`${stats.users_by_role.length} roles`} color="#1a237e" onClick={() => navigate('/admin/users')} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Kpi icon={<AttachMoneyIcon />} label="Active MRR" value={`$${activeMRR}`} sub={`+$${(potentialMRR - activeMRR).toFixed(0)} potential from trials`} color="#2e7d32" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Kpi icon={<HourglassBottomIcon />} label="Trials ending soon" value={stats.trials_ending.in_30_days} sub={`${stats.trials_ending.in_7_days} in next 7 days · ${stats.trials_ending.already_expired} expired`} color={stats.trials_ending.in_7_days > 0 ? '#c62828' : '#ef6c00'} onClick={() => navigate('/admin/users')} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Kpi icon={<WarningAmberIcon />} label="Subs over limit" value={stats.over_limit_subs} sub="Upgrade candidates" color={stats.over_limit_subs > 0 ? '#c62828' : '#9e9e9e'} onClick={() => navigate('/admin/users')} />
          </Grid>
        </Grid>

        {/* Two-column breakdown */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Users by role */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1a237e', mb: 2 }}>Users by role</Typography>
              {stats.users_by_role.map(r => {
                const pct = (r.cnt / totalUsers) * 100;
                return (
                  <Box key={r.role} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{r.role.replace('_', ' ')}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: roleColor[r.role] || '#1a237e' }}>{r.cnt}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3, bgcolor: '#eceff1', '& .MuiLinearProgress-bar': { bgcolor: roleColor[r.role] || '#1a237e' } }} />
                  </Box>
                );
              })}
            </Paper>
          </Grid>

          {/* Subscriptions by status */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1a237e', mb: 2 }}>Subscriptions by status</Typography>
              {subSubs === 0 ? (
                <Typography variant="body2" color="text.secondary">No subscriptions yet.</Typography>
              ) : stats.subscriptions_by_status.map(s => {
                const pct = (s.cnt / subSubs) * 100;
                return (
                  <Box key={s.status} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{s.status.replace('_', ' ')}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: statusColor[s.status] || '#1a237e' }}>{s.cnt}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3, bgcolor: '#eceff1', '& .MuiLinearProgress-bar': { bgcolor: statusColor[s.status] || '#1a237e' } }} />
                  </Box>
                );
              })}
            </Paper>
          </Grid>
        </Grid>

        {/* Feedback + Activity */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2.5, borderRadius: 2, cursor: 'pointer' }} onClick={() => navigate('/admin/feedback')}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1a237e' }}>Feedback queue</Typography>
                <Chip size="small" label={`${openFb} open / ${totalFb} total`} sx={{ fontWeight: 600 }} />
              </Stack>
              {totalFb === 0 ? (
                <Typography variant="body2" color="text.secondary">No feedback yet.</Typography>
              ) : (
                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {stats.feedback_by_status.map(f => (
                    <Box key={f.status} sx={{ minWidth: 80 }}>
                      <Typography sx={{ fontSize: 22, fontWeight: 800, color: fbColor[f.status] }}>{f.cnt}</Typography>
                      <Typography variant="caption" sx={{ color: '#666', textTransform: 'capitalize' }}>{f.status.replace('_', ' ')}</Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1a237e', mb: 2 }}>System activity</Typography>
              <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
                <Box>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1a237e' }}>{stats.activity.active_contracts}</Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>Active contracts</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#ef6c00' }}>{stats.activity.unpaid_invoices}</Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>Unpaid invoices</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0288d1' }}>{stats.activity.open_proposals}</Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>Open proposals</Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
}
